import React, { useEffect, useState, useCallback } from "react";
import { useElementOverrides } from "@/contexts/ElementOverrideContext";
import {
  inferCategory,
  detectElementCategory,
  extractComputedColors,
  generateElementLabel,
  isGenericDesignId,
  ELEMENT_LABELS,
  SelectedElement,
  ElementCategory,
} from "@/lib/elementOverrideTypes";

/**
 * Full-screen overlay that activates in "select mode".
 * 
 * ARCHITECTURE: When the user clicks any element, we walk UP to find the
 * nearest ancestor with a stable `data-design-id`. We then detect what
 * sub-part was clicked (icon, text, etc.) and set the category accordingly.
 * 
 * This ensures we always use stable, code-defined design IDs — never
 * dynamically generated ones.
 */
export function ElementSelectionOverlay() {
  const { isSelectMode, setSelectMode, setSelectedElement } = useElementOverrides();
  const [hoveredRect, setHoveredRect] = useState<DOMRect | null>(null);
  const [hoveredLabel, setHoveredLabel] = useState("");

  const isOverlayElement = useCallback((el: HTMLElement): boolean => {
    return !!el.closest("[data-picker-overlay]");
  }, []);

  /** Walk up to find nearest ancestor with a stable data-design-id */
  const findDesignAncestor = useCallback((el: HTMLElement): HTMLElement | null => {
    let current: HTMLElement | null = el;
    let maxSteps = 15;
    while (current && maxSteps-- > 0) {
      if (current.dataset.designId) {
        const id = current.dataset.designId;
        // Skip generic IDs (like page-bg) when clicking specific sub-elements
        if (isGenericDesignId(id)) {
          current = current.parentElement;
          continue;
        }
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }, []);

  /** Detect what sub-part of the card was clicked to set the right category */
  const detectSubCategory = useCallback((clickedEl: HTMLElement, containerEl: HTMLElement): ElementCategory => {
    const tag = clickedEl.tagName.toLowerCase();
    // Clicked on an SVG or inside one → icon editing
    if (tag === "svg" || clickedEl.closest("svg")) return "icon";
    // Clicked on text
    if (["span", "p", "h1", "h2", "h3", "h4", "label"].includes(tag)) return "text";
    // Clicked on the container itself → full card editing
    if (clickedEl === containerEl) return inferCategory(containerEl.dataset.designId || "");
    // Default: use the container's inferred category
    return inferCategory(containerEl.dataset.designId || "");
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const rawEl = e.target as HTMLElement;
    // If hovering the overlay, peek underneath
    const el = (() => {
      if (rawEl && isOverlayElement(rawEl)) {
        const overlays = document.querySelectorAll<HTMLElement>('[data-picker-overlay]');
        overlays.forEach(o => o.style.visibility = 'hidden');
        const underneath = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
        overlays.forEach(o => o.style.visibility = '');
        return underneath;
      }
      return rawEl;
    })();

    if (!el || el === document.documentElement || el === document.body) {
      setHoveredRect(null);
      setHoveredLabel("");
      return;
    }

    // Find the design ancestor to highlight the whole card
    const designAncestor = findDesignAncestor(el);
    if (designAncestor) {
      setHoveredRect(designAncestor.getBoundingClientRect());
      const id = designAncestor.dataset.designId!;
      setHoveredLabel(ELEMENT_LABELS[id] || generateElementLabel(designAncestor));
    } else {
      setHoveredRect(el.getBoundingClientRect());
      setHoveredLabel(generateElementLabel(el));
    }
  }, [isOverlayElement, findDesignAncestor]);

  const handleClick = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // If clicking the overlay itself, find the element underneath
    const clickedEl = (() => {
      const target = e.target as HTMLElement;
      if (isOverlayElement(target)) {
        // Temporarily hide overlay to find element underneath
        const overlays = document.querySelectorAll<HTMLElement>('[data-picker-overlay]');
        overlays.forEach(o => o.style.visibility = 'hidden');
        const underneath = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
        overlays.forEach(o => o.style.visibility = '');
        return underneath;
      }
      return target;
    })();

    if (!clickedEl) return;

    // Find the nearest stable design-id ancestor
    const designAncestor = findDesignAncestor(clickedEl);
    
    // Use design ancestor if found, otherwise use the clicked element directly
    const targetEl = designAncestor || clickedEl;
    const designId = designAncestor?.dataset.designId || generateElementLabel(clickedEl).replace(/\s+/g, '-').toLowerCase();
    // Ensure the DOM element has the design-id attribute so DOM fallback can find it
    if (!targetEl.dataset.designId) {
      targetEl.dataset.designId = designId;
    }
    const label = (designAncestor && ELEMENT_LABELS[designAncestor.dataset.designId!]) || generateElementLabel(targetEl);
    const category = designAncestor 
      ? detectSubCategory(clickedEl, designAncestor) 
      : detectElementCategory(clickedEl);
    const computedColors = extractComputedColors(clickedEl);

    const selected: SelectedElement = {
      designId,
      label,
      category,
      element: targetEl,
      actualElement: clickedEl !== targetEl ? clickedEl : undefined,
      computedColors,
    };
    setSelectedElement(selected);
    setSelectMode(false);
  }, [setSelectedElement, setSelectMode, isOverlayElement, findDesignAncestor, detectSubCategory]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      setSelectMode(false);
    }
  }, [setSelectMode]);

  useEffect(() => {
    if (!isSelectMode) return;
    document.addEventListener("mousemove", handleMouseMove, true);
    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove, true);
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isSelectMode, handleMouseMove, handleClick, handleKeyDown]);

  if (!isSelectMode) return null;

  return (
    <>
      <div
        data-picker-overlay
        className="fixed inset-0 z-[9990] cursor-crosshair"
        style={{ pointerEvents: "all", background: "rgba(0,0,0,0.15)" }}
        onClick={(e) => {
          // Let the document-level handleClick process it
          // This div just ensures clicks aren't swallowed by underlying elements
        }}
      />
      {hoveredRect && (
        <div
          data-picker-overlay
          className="fixed z-[9991] pointer-events-none rounded"
          style={{
            left: hoveredRect.left - 2,
            top: hoveredRect.top - 2,
            width: hoveredRect.width + 4,
            height: hoveredRect.height + 4,
            border: "2px solid hsl(var(--accent))",
            boxShadow: "0 0 0 2000px rgba(0,0,0,0.25), 0 0 8px hsl(var(--accent) / 0.6)",
            background: "rgba(255,165,0,0.08)",
          }}
        >
          {hoveredLabel && (
            <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-xs px-2 py-0.5 rounded-md whitespace-nowrap font-medium shadow-md">
              {hoveredLabel}
            </div>
          )}
        </div>
      )}
      <div data-picker-overlay className="fixed top-4 left-1/2 -translate-x-1/2 z-[9992] bg-card border-2 border-accent rounded-xl px-4 py-2 shadow-lg" dir="rtl">
        <span className="text-sm font-medium text-foreground">
          🎯 לחצו על כל אלמנט (אייקון, טקסט, כפתור) כדי לערוך אותו • <button onClick={() => setSelectMode(false)} className="text-accent underline">ביטול (Esc)</button>
        </span>
      </div>
    </>
  );
}
